import { Link } from "react-router-dom";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import { useScrollReveal } from "../../hooks/useScrollReveal";
import JsonLd from "../../components/feature/JsonLd";

const siteUrl = import.meta.env.VITE_SITE_URL ?? "https://example.com";

const aboutSchema = {
  "@context": "https://schema.org",
  "@type": "AboutPage",
  "name": "Über SPDB Studio – Kreativbüro für Luxusimmobilien Zürich",
  "description": "SPDB Studio ist ein auf das Luxussegment spezialisiertes Kreativbüro aus Zürich. Ästhetisches Gespür, absolute Diskretion und Schweizer Präzision für Luxusimmobilien ab 2 Mio. CHF.",
  "url": `${siteUrl}/ueber-uns`,
  "publisher": {
    "@type": "Organization",
    "name": "SPDB Studio",
    "url": siteUrl,
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Bahnhofstrasse 42",
      "addressLocality": "Zürich",
      "postalCode": "8001",
      "addressCountry": "CH"
    }
  }
};

const chapters = [
  {
    num: "I",
    label: "Das Studio",
    title: "Wir bauen keine Webseiten. Wir schaffen Begehrlichkeit.",
    body: [
      "SPDB Studio ist ein Kreativbüro aus Zürich — spezialisiert auf die visuelle und digitale Inszenierung von Luxusimmobilien. Was wir tun, klingt einfach: Wir nehmen Ihr Bildmaterial und verwandeln es in eine digitale Markenwelt.",
      "Was dabei entsteht, ist mehr als eine Landingpage. Es ist die erste Begegnung eines potenziellen Käufers mit einem Objekt, das vielleicht sein Leben verändert. Diese Begegnung gestalten wir.",
    ],
    image: "https://public.readdy.ai/ai/img_res/5e775f218ed15dc3bf5f0163781177fa.jpg",
  },
  {
    num: "II",
    label: "Unser Anspruch",
    title: "Luxus braucht keine Lautstärke.",
    body: [
      "Wer eine Immobilie für mehrere Millionen kauft, wird nicht von bunten Bannern oder generischen Grundrissen überzeugt. Er wird von einem Gefühl überzeugt — lange bevor ein Notar involviert ist.",
      "Unser Job ist es, dieses Gefühl zu erzeugen. Mit einer Bildsprache, die atmet. Mit Texten, die nicht verkaufen, sondern einladen. Mit digitalen Präsenzen, die das Objekt zu einem Erlebnis machen.",
    ],
    image: "https://readdy.ai/api/search-image?query=swiss-luxury-brand-creative-director-reviewing-property-branding-materials-high-quality-print-on-white-desk-studio-photography-natural-light-editorial-minimal-gold-accents&width=700&height=500&seq=about_ch2&orientation=landscape",
  },
  {
    num: "III",
    label: "Unser Fokus",
    title: "Ein Markt. Eine Sprache. Perfektioniert.",
    body: [
      "Wir arbeiten bewusst ausschließlich mit Maklern im Segment ab CHF 2 Millionen. Nicht weil wir exklusiv sein wollen — sondern weil unser Ansatz eine Grundqualität voraussetzt, die unterhalb dieser Schwelle selten existiert.",
      "Dieser Fokus ist keine Einschränkung. Er ist unsere Stärke. Wir kennen diesen Markt, seine Käufer, ihre Erwartungen. Und wir sprechen ihre Sprache — mit jedem Pixel, das wir gestalten.",
    ],
    image: "https://readdy.ai/api/search-image?query=luxury-real-estate-architecture-photography-zurich-lake-property-premium-modern-villa-exterior-golden-hour-light-professional-editorial-minimal-background-swiss-quality&width=700&height=500&seq=about_ch3&orientation=landscape",
  },
];

const values = [
  {
    num: "01",
    icon: "ri-eye-line",
    title: "Ästhetisches Gespür",
    desc: "Jede Bildkomposition, jedes Licht, jede typografische Entscheidung ist kein Zufall — sondern eine bewusste Aussage über den Wert eines Objekts.",
  },
  {
    num: "02",
    icon: "ri-lock-line",
    title: "Absolute Diskretion",
    desc: "Alle Projekte, Daten und Kundenbeziehungen behandeln wir mit vollständiger Vertraulichkeit. Das ist keine Option — es ist unser Fundament.",
  },
  {
    num: "03",
    icon: "ri-focus-3-line",
    title: "Schweizer Präzision",
    desc: "Kein Detail ist klein genug, um ignoriert zu werden. Jede Deadline sitzt. Jedes Asset wird auf Pixel-Niveau kontrolliert.",
  },
  {
    num: "04",
    icon: "ri-heart-line",
    title: "Echte Leidenschaft",
    desc: "Wir arbeiten nicht für alle. Wir arbeiten für die, die Qualität nicht erklären müssen. Dieser Fokus macht den Unterschied.",
  },
];

const stats = [
  { value: "2 Mio.+", label: "Objektwert ab CHF" },
  { value: "100%", label: "Fokus auf Luxussegment" },
  { value: "Zürich", label: "Heimatmarkt & Herzstück" },
];

function ChapterBlock({
  chapter,
  index,
}: {
  chapter: (typeof chapters)[0];
  index: number;
}) {
  const { ref, isVisible } = useScrollReveal(0.1);
  const isEven = index % 2 === 0;

  return (
    <div
      ref={ref}
      className={`grid grid-cols-1 lg:grid-cols-2 gap-16 items-center py-20 border-b border-light-gray last:border-0 transition-all duration-800 ${
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
      }`}
    >
      {/* Image */}
      <div className={`${isEven ? "order-1 lg:order-1" : "order-1 lg:order-2"}`}>
        <div className="relative overflow-hidden" style={{ aspectRatio: "7/5" }}>
          <img
            src={chapter.image}
            alt={chapter.title}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute top-6 left-6 font-serif text-7xl text-white/15 font-semibold select-none">
            {chapter.num}
          </div>
        </div>
      </div>

      {/* Text */}
      <div className={`${isEven ? "order-2 lg:order-2" : "order-2 lg:order-1"}`}>
        <span className="font-serif text-6xl text-gold/15 font-semibold">{chapter.num}</span>
        <p className="section-label mt-2 mb-4">{chapter.label}</p>
        <h2 className="font-serif text-3xl lg:text-4xl text-text-primary font-medium leading-tight mb-5">
          {chapter.title}
        </h2>
        <span className="gold-divider-left mb-7" />
        {chapter.body.map((p, i) => (
          <p key={i} className="font-sans font-light text-text-secondary text-base leading-relaxed mb-4 last:mb-0">
            {p}
          </p>
        ))}
      </div>
    </div>
  );
}

export default function UeberUnsPage() {
  const { ref: heroRef, isVisible: heroVisible } = useScrollReveal(0.1);
  const { ref: valuesRef, isVisible: valuesVisible } = useScrollReveal(0.1);
  const { ref: statsRef, isVisible: statsVisible } = useScrollReveal(0.1);

  return (
    <>
      <title>Über Uns – SPDB Studio Zürich | Kreativbüro für Luxusimmobilien</title>
      <meta name="description" content="SPDB Studio ist ein auf das Luxussegment spezialisiertes Kreativbüro aus Zürich. Wir arbeiten diskret, präzise und mit einem einzigen Ziel: Das Außergewöhnliche sichtbar machen. Luxusimmobilien ab 2 Mio. CHF." />
      <meta name="keywords" content="SPDB Studio Über Uns, Kreativbüro Zürich, Luxusimmobilien Branding, Immobilien Marketing Schweiz" />
      <link rel="canonical" href={`${siteUrl}/ueber-uns`} />
      <JsonLd data={aboutSchema} />
      <main className="min-h-screen bg-white">
        <Navbar />

        {/* Hero — centered like Makler Service */}
        <section className="pt-48 pb-24 px-8 lg:px-16 bg-warm-white">
          <div
            ref={heroRef}
            className={`max-w-3xl mx-auto text-center transition-all duration-800 ${
              heroVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
          >
            <p className="section-label mb-6">Wer wir sind</p>
            <h1 className="font-serif text-5xl md:text-6xl text-text-primary font-medium leading-tight mb-6">
              Weniger Lärm.
              <br />
              <em className="italic text-gold">Mehr Wirkung.</em>
            </h1>
            <span className="gold-divider mb-8" />
            <p className="font-sans font-light text-text-secondary text-base leading-relaxed max-w-xl mx-auto">
              SPDB Studio ist ein Kreativbüro aus Zürich — spezialisiert auf die visuelle und digitale Inszenierung von Luxusimmobilien. Was uns antreibt: das Außergewöhnliche sichtbar machen.
            </p>
          </div>
        </section>

        {/* Chapters — alternating like ServiceBlock */}
        <section className="py-20 px-8 lg:px-16">
          <div className="max-w-screen-xl mx-auto">
            {chapters.map((chapter, index) => (
              <ChapterBlock key={chapter.num} chapter={chapter} index={index} />
            ))}
          </div>
        </section>

        {/* Values — bordered grid with texture */}
        <section className="py-32 px-8 lg:px-16 relative overflow-hidden bg-white">
          {/* Marble texture background */}
          <div className="absolute inset-0 pointer-events-none">
            <img
              src="https://readdy.ai/api/search-image?query=soft-light-luxury-marble-texture-surface-white-cream-subtle-veining-abstract-minimal-background-high-resolution-elegant-swiss-design-inspiration&width=1920&height=900&seq=valsbg001&orientation=landscape"
              alt=""
              className="w-full h-full object-cover object-center opacity-[0.04]"
            />
          </div>

          <div className="max-w-screen-xl mx-auto relative z-10">
            {/* Section header — split layout */}
            <div
              ref={valuesRef}
              className={`flex flex-col items-center text-center mb-24 transition-all duration-800 ${
                valuesVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
              }`}
            >
              <div>
                <p className="section-label mb-6">Unsere Werte</p>
                <h2 className="font-serif text-4xl md:text-5xl text-text-primary font-medium leading-tight">
                  Was uns
                  <br />
                  <em className="italic text-gold">antreibt.</em>
                </h2>
              </div>
              <div className="mt-10 max-w-xl">
                <span className="gold-divider-left mb-7" />
                <p className="font-sans font-light text-text-secondary text-base leading-relaxed">
                  Vier Grundsätze, die jede Entscheidung leiten — vom ersten Konzept bis zur finalen Übergabe. Nicht als Versprechen. Als Haltung.
                </p>
              </div>
            </div>

            {/* Values grid — open bordered cells with hover */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 divide-y md:divide-y-0 md:divide-x divide-light-gray border border-light-gray">
              {values.map((v, i) => (
                <div
                  key={v.title}
                  className={`group p-10 flex flex-col gap-0 transition-all duration-500 hover:bg-warm-white cursor-default ${
                    valuesVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
                  }`}
                  style={{ transitionDelay: `${i * 120}ms` }}
                >
                  {/* Large faint number */}
                  <span className="font-serif text-8xl text-gold/[0.07] group-hover:text-gold/[0.13] font-semibold leading-none select-none mb-5 transition-colors duration-500">
                    {v.num}
                  </span>
                  {/* Icon row */}
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-9 h-9 flex items-center justify-center border border-gold/30 group-hover:border-gold/60 transition-colors duration-500">
                      <i className={`${v.icon} text-gold text-base`} />
                    </div>
                  </div>
                  {/* Title */}
                  <h4 className="font-serif text-xl text-text-primary font-medium mb-3 leading-snug group-hover:text-gold transition-colors duration-500">
                    {v.title}
                  </h4>
                  {/* Gold micro-line — animates on hover */}
                  <div className="w-8 h-px bg-gold/30 mb-4 group-hover:w-14 group-hover:bg-gold/70 transition-all duration-500" />
                  {/* Desc */}
                  <p className="font-sans font-light text-text-secondary text-sm leading-relaxed">
                    {v.desc}
                  </p>
                </div>
              ))}
            </div>

            {/* Manifesto line below grid */}
            <div
              className={`mt-16 text-center transition-all duration-800 ${
                valuesVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
              }`}
              style={{ transitionDelay: "600ms" }}
            >
              <p className="font-serif text-lg text-text-muted italic font-light max-w-xl mx-auto leading-relaxed">
                „Qualität ist keine Eigenschaft — sie ist eine Entscheidung, die man täglich trifft."
              </p>
            </div>
          </div>
        </section>

        {/* Stats bar */}
        <section
          ref={statsRef}
          className={`py-16 px-8 lg:px-16 border-t border-b border-gold/20 transition-all duration-800 ${
            statsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          <div className="max-w-screen-xl mx-auto grid grid-cols-3 gap-8">
            {stats.map((s) => (
              <div key={s.label} className="text-center">
                <p className="font-serif text-4xl text-text-primary font-medium mb-2">{s.value}</p>
                <p className="font-sans text-xs uppercase tracking-widest text-text-secondary">{s.label}</p>
              </div>
            ))}
          </div>
        </section>

        {/* CTA — like Makler Service */}
        <section className="py-32 px-8 lg:px-16 text-center bg-warm-white">
          <div className="max-w-xl mx-auto">
            <p className="section-label mb-6">Zusammenarbeiten</p>
            <h2 className="font-serif text-4xl md:text-5xl text-text-primary font-medium leading-tight mb-6">
              Lassen Sie uns über Ihr Objekt sprechen.
            </h2>
            <span className="gold-divider mb-10" />
            <p className="font-sans font-light text-text-secondary text-base leading-relaxed mb-10">
              Jede Anfrage behandeln wir mit absoluter Diskretion. Kein Verkaufsgespräch — nur ein Gespräch unter Profis.
            </p>
            <Link to="/kontakt" className="btn-gold-fill">
              Diskrete Anfrage stellen
            </Link>
          </div>
        </section>

        <Footer />
      </main>
    </>
  );
}
